import isPlainObject from 'lodash/isPlainObject';

import { placeholder } from '@webapp/common/lib/ui';
import {
    CompletePageType,
    PasswordAccessType,
    RecipientPhoneAuthType,
    WelcomePageType,
} from '@webapp/common/resources/survey';

export enum WebHookSendMode {
    NOW = 'now',
    PACK = 'pack',
}

export enum WebHookFormat {
    JSON = 'JSON',
    XML = 'XML',
}

export interface SurveyAddonMain {
    audio: boolean;
    agreementText: string;

    welcomePage: WelcomePageType;
    welcomePageText: string;

    completePage: CompletePageType;
    completePageText: string;
    completeWebsite: string;
    completeSurvey: number;

    // Квота опроса набралась, пока респондент проходил: вместо страницы завершения
    quotaFilledText: string;

    // Общая квота опроса уже набрана до прохождения (start/response вернул
    // quotaFilled): вместо начала опроса показывается этот текст (#764).
    quotaFilledStartText: string;

    disabledSurveyText: string;
    retryDisabledSurveyText: string;

    showResultTest: boolean;
    showDoneTest: boolean;
    showAgreementText: boolean;

    copyUrlParams: boolean;

    savingNotComplete: boolean;
    disableMetric: boolean;

    phoneAuth: boolean;
    disallowRefillingPhoneAuth: boolean;
    recipientPhoneAuth: boolean;
    recipientPhoneAuthType: RecipientPhoneAuthType;
    recipientPhoneAuthSmsText: string;
    authEmailLinkParam?: string;

    passwordType: PasswordAccessType;
    passwordAccess: boolean;
    passwordLabel: string;
    wrongPasswordLabel: string;

    individualPasswords: boolean;
    passwordWasActivated: string;
    surveyWasBlocked: string;
    anonymousPassword: boolean;

    vkAuth: boolean;

    timer: boolean;
    timerHours: number;
    timerMinutes: number;
    timerSeconds: number;
    timerOn: number;

    webHookFormat: WebHookFormat;
    webHookAuthorization: boolean;
    webHookAuthorizationToken: string;

    replyWebHook: boolean;
    replyWebHookUrl: string;
    replyWebHookSend: WebHookSendMode;

    surveyTemplateWebHook: boolean;
    surveyTemplateWebHookUrl: string;

    yaMetrics: boolean;
    yaMetricsId: string;
}

export const initialSurveyAddonMain: Readonly<SurveyAddonMain> = {
    audio: false,
    agreementText: 'Начиная прохождение опроса, я даю согласие на обработку моих персональных данных',

    welcomePage: WelcomePageType.TO_SINGLE_PAGE,
    welcomePageText: 'Мы не говорим, что этот опрос решит судьбу вселенной. Но и не отрицаем.',

    completePage: CompletePageType.SUCCESS_TEXT,
    completePageText:
        '<div style="text-align: center; font-size: 24px;"><div style="font-size: 36px;">&#x2764;&#xfe0f;</div><span>Спасибо за уделенное время!</span></div>',
    completeWebsite: '',
    completeSurvey: null,

    quotaFilledText:
        '<p style="text-align:center;"><span style="font-size:24px;"><strong>Спасибо за участие!</strong></span></p><p style="text-align:center;">&nbsp;</p><p style="text-align:center;"><span style="font-size:24px;">Пока вы проходили этот опрос, необходимое количество ответов уже было собрано. Ваш ответ не будет включен в результаты исследования.</span></p>',

    // Дефолт совпадает с бэковым SurveyParam::MAIN_QUOTA_FILLED_TEXT_DEFAULT (#764)
    quotaFilledStartText:
        '<div style="text-align: center; font-size: 24px;">Спасибо за интерес к опросу. Сбор ответов уже завершён.</div>',

    copyUrlParams: false,

    disabledSurveyText:
        '<div style="text-align: center; font-size: 24px;"><div>Страница недоступна…<span style="font-size: 36px;">🤔</span></div><span>Обратитесь к человеку, приславшему вам ссылку!</span></div>',
    retryDisabledSurveyText:
        '<div style="text-align: center; font-size: 24px;"><div>Спасибо, что снова заглянули!</div><span>Похоже, вы уже прошли этот опрос. Повторное участие здесь не требуется.</span></div>',
    showResultTest: true,
    showDoneTest: true,
    showAgreementText: false,

    savingNotComplete: false,
    disableMetric: false,

    phoneAuth: false,
    disallowRefillingPhoneAuth: false,
    recipientPhoneAuth: false,
    recipientPhoneAuthType: RecipientPhoneAuthType.SMS_AND_PHONE,
    recipientPhoneAuthSmsText: 'Ваш код подтверждения на сайте Testograf - {code}',

    passwordType: PasswordAccessType.NOT,
    passwordAccess: false,
    passwordLabel: 'Пожалуйста, введите пароль',
    wrongPasswordLabel: 'Неверный пароль',

    individualPasswords: false,
    anonymousPassword: false,
    passwordWasActivated: 'Данный пароль уже был активирован',
    surveyWasBlocked: placeholder('Доступ к опросу заблокирован'),

    vkAuth: false,

    timer: false,
    timerHours: 0,
    timerMinutes: 30,
    timerSeconds: 0,
    timerOn: null, // null - immediately after start

    webHookFormat: WebHookFormat.XML,
    webHookAuthorization: false,
    webHookAuthorizationToken: null,

    replyWebHook: false,
    replyWebHookUrl: '',
    replyWebHookSend: WebHookSendMode.PACK,

    surveyTemplateWebHook: false,
    surveyTemplateWebHookUrl: '',

    yaMetrics: false,
    yaMetricsId: '',
};

// В старых сохранениях timerOn мог оказаться объектом (опция/вопрос селекта) вместо id —
// тогда таймер не запускался ни на клиенте, ни на бэке (FRONT-MAA). Достаём id из известных
// полей и приводим числовые строки к числу; нераспознанные значения оставляем как есть,
// чтобы поведение не менялось, а Sentry показывал реальный shape.
export const normalizeTimerOn = (timerOn: any): any => {
    const extractId = (value: any): number | null | undefined => {
        if (value === null) return null;
        if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
        if (typeof value === 'string' && value.trim() !== '' && !isNaN(Number(value))) return Number(value);
        return undefined;
    };

    let candidate = timerOn;

    if (isPlainObject(timerOn)) {
        // null в value/id — валидное «Начало опроса (сразу)», поэтому проверяем именно undefined
        if (timerOn.value !== undefined) {
            candidate = timerOn.value;
        } else if (timerOn.id !== undefined) {
            candidate = timerOn.id;
        } else {
            candidate = timerOn.ID;
        }
    }

    const id = extractId(candidate);

    return id === undefined ? timerOn : id;
};
